/**
 * ActivityLogPanel — real-time audit trail for PIM changes.
 * Shows last N rows from activity_log table with diff expansion.
 */
import { useCallback, useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Clock, ChevronDown, RefreshCw, Loader2 } from 'lucide-react';
import { ActivityLogEntry, getActivityLog } from '@/api';

const ACTION_STYLE: Record<string, string> = {
  create: 'bg-emerald-500/15 text-emerald-400',
  update: 'bg-blue-500/15 text-blue-400',
  delete: 'bg-red-500/15 text-red-400',
  patch: 'bg-yellow-500/15 text-yellow-400',
};

const TABLE_LABEL: Record<string, string> = {
  v3_price_tiers: 'Тир цены',
  v3_system_motor_links: 'Мотор',
  catalog_systems: 'Система',
  catalog_system_variants: 'Вариант',
};

function relativeTime(iso: string | null): string {
  if (!iso) return '—';
  const diff = (Date.now() - new Date(iso).getTime()) / 1000;
  if (diff < 60) return `${Math.round(diff)}с назад`;
  if (diff < 3600) return `${Math.round(diff / 60)}м назад`;
  if (diff < 86400) return `${Math.round(diff / 3600)}ч назад`;
  return new Date(iso).toLocaleDateString('ru');
}

function DiffView({ old_values, new_values }: { old_values: Record<string, unknown> | null; new_values: Record<string, unknown> | null }) {
  const keys = Array.from(new Set([...Object.keys(old_values ?? {}), ...Object.keys(new_values ?? {})]));
  if (!keys.length) return <p className="text-xs text-white/30 italic">нет данных</p>;
  return (
    <div className="space-y-1">
      {keys.map((k) => {
        const oldV = old_values?.[k];
        const newV = new_values?.[k];
        const changed = JSON.stringify(oldV) !== JSON.stringify(newV);
        return (
          <div key={k} className={`flex items-start gap-2 text-[11px] px-2 py-1 rounded ${changed ? 'bg-white/[0.04]' : ''}`}>
            <span className="text-white/40 shrink-0 w-32 truncate">{k}</span>
            {changed ? (
              <>
                <span className="text-red-400/80 line-through truncate max-w-[100px]">{String(oldV ?? '—')}</span>
                <span className="text-white/30 shrink-0">→</span>
                <span className="text-emerald-400/90 truncate max-w-[100px]">{String(newV ?? '—')}</span>
              </>
            ) : (
              <span className="text-white/50">{String(newV ?? '—')}</span>
            )}
          </div>
        );
      })}
    </div>
  );
}

function LogRow({ entry }: { entry: ActivityLogEntry }) {
  const [expanded, setExpanded] = useState(false);
  const hasDetail = entry.old_values || entry.new_values;
  return (
    <div className="border border-white/[0.06] rounded-xl bg-white/[0.02] overflow-hidden">
      <button
        onClick={() => hasDetail && setExpanded((v) => !v)}
        className={`flex items-center gap-3 w-full px-4 py-3 text-left transition-colors ${hasDetail ? 'hover:bg-white/[0.03]' : ''}`}
      >
        <span className={`px-1.5 py-0.5 rounded text-[10px] font-semibold uppercase shrink-0 ${ACTION_STYLE[entry.action] ?? 'bg-white/10 text-white/50'}`}>
          {entry.action}
        </span>
        <span className="text-xs text-white/70 font-medium shrink-0">
          {TABLE_LABEL[entry.table_name] ?? entry.table_name}
          <span className="text-white/30 font-normal"> #{entry.record_id}</span>
        </span>
        <span className="text-[11px] text-white/30 ml-auto shrink-0 flex items-center gap-1">
          <Clock size={11} />
          {relativeTime(entry.created_at)}
        </span>
        {hasDetail && (
          <ChevronDown
            size={14}
            className={`text-white/30 shrink-0 transition-transform ${expanded ? 'rotate-180' : ''}`}
          />
        )}
      </button>
      <AnimatePresence>
        {expanded && hasDetail && (
          <motion.div
            initial={{ height: 0 }}
            animate={{ height: 'auto' }}
            exit={{ height: 0 }}
            className="overflow-hidden"
          >
            <div className="px-4 pb-3 border-t border-white/[0.05]">
              <DiffView old_values={entry.old_values} new_values={entry.new_values} />
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

interface ActivityLogPanelProps {
  tableName?: string;
  recordId?: number;
  limit?: number;
}

export function ActivityLogPanel({ tableName, recordId, limit = 40 }: ActivityLogPanelProps) {
  const [entries, setEntries] = useState<ActivityLogEntry[]>([]);
  const [loading, setLoading] = useState(false);
  const [lastFetch, setLastFetch] = useState<number>(0);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await getActivityLog({ table_name: tableName, record_id: recordId, limit });
      setEntries(data);
      setLastFetch(Date.now());
    } catch {
      // silent
    } finally {
      setLoading(false);
    }
  }, [tableName, recordId, limit]);

  useEffect(() => { void load(); }, [load]);

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-medium text-white/70">История изменений</h3>
        <button
          onClick={load}
          disabled={loading}
          className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs text-white/50 hover:text-white/80 hover:bg-white/5 transition-colors"
        >
          {loading ? <Loader2 size={12} className="animate-spin" /> : <RefreshCw size={12} />}
          Обновить
        </button>
      </div>

      {lastFetch > 0 && (
        <p className="text-[11px] text-white/25">
          Обновлено {relativeTime(new Date(lastFetch).toISOString())}
        </p>
      )}

      {loading && entries.length === 0 ? (
        <div className="flex items-center justify-center py-10 text-white/30 text-sm">
          <Loader2 size={16} className="animate-spin mr-2" />
          Загрузка...
        </div>
      ) : entries.length === 0 ? (
        <div className="text-center py-10 text-sm text-white/30">
          Нет записей
        </div>
      ) : (
        <div className="space-y-2">
          {entries.map((e) => <LogRow key={e.id} entry={e} />)}
        </div>
      )}
    </div>
  );
}
